import React from 'react';
import { FormattedMessage } from 'react-intl';
import cx from 'classnames';
import type * as Types from '@xingternal/pages-graphql-types';
import type { NewsProps } from '../News';

import {
  FormattedTimestamp,
  usePageContext,
  useEditContext,
} from '@xingternal/pages-common';
import Userbadge, { TYPE_INSIDER } from '@xingternal/malt-userbadge';
import { ProfileImage } from '@xingternal/profile-image';
import { FollowContainer } from '@xingternal/pages-header';

import { getActionTextIdByType } from '../utils';
import { DotSeparator } from './DotSeparator';
import styles from './styles.css';
import { NewsStatusFlag } from './NewsStatusFlag';

import { PAGES_URL_PREFIX, PINNED } from '../constants';

type NewsItemActorProps = Pick<NewsProps, 'actor' | 'isFollowingPage'> & {
  pageId: string;
  actionType?:
    | 'EntityPageContentFeedArticlePost'
    | 'EntityPageContentFeedCompanyArticlePost'
    | 'EntityPageContentFeedPlainTextPostingPost'
    | 'EntityPageContentFeedPostingPost'
    | 'EntityPageContentFeedArticlesArticlePost';
  publicationState?: Types.PostingPublicationState;
  showActor?: boolean;
  isPinned?: boolean;
  date: string;
};

const NewsItemActor = ({
  actionType,
  actor,
  date,
  isFollowingPage = false,
  isPinned,
  pageId,
  publicationState,
  showActor,
}: NewsItemActorProps) => {
  const { pageContext } = usePageContext();
  const { isEditing } = useEditContext();
  const { focusType } = pageContext;

  let urlPath = PAGES_URL_PREFIX;

  switch (focusType) {
    // case EntityPageFocusType.<SOMETHING ELSE>:
    //   urlPath = ANOTHER_PREFIX;
    //   break;
    default:
      urlPath = PAGES_URL_PREFIX;
      break;
  }

  // URLs here are assumed to start with a slash '/'
  const permalink = `${urlPath}/${actor.slug}`;
  const url = `${permalink}${
    permalink.includes('?') ? '&' : '?'
  }sc_o=entity_page_news_module_to_publisher`;

  const actionTextId = getActionTextIdByType(actionType);
  const itemStatusState = isPinned ? PINNED : publicationState;

  // whether to show `X posted this article` legend or not
  let displayAction = false;

  if (isEditing) {
    if (!publicationState && !isPinned) {
      displayAction = true;
    }
  } else {
    if (!isPinned) {
      displayAction = true;
    }
  }

  const showFollowButton = false; // following is currently disabled here
  return (
    <>
      {showActor ? (
        <section className={styles.actor}>
          <a className={styles.actorImgAnchor} href={url}>
            <ProfileImage
              size="small"
              type="company"
              profileName={actor.title}
              alt={actor.title}
              className={styles.actorImg}
              src={actor.logo}
            />
          </a>
          <section className={styles.actorContent}>
            <span className={styles.actorNameWrap}>
              <a className={styles.actorName} href={url}>
                {actor.title}
              </a>
              {/* TODO: Refactor insider condition */}
              {actor.isInsider && (
                <Userbadge
                  outline
                  type={TYPE_INSIDER}
                  className={styles.badge}
                />
              )}
            </span>
            <span className={styles.actorSubtitle}>
              {((isEditing && publicationState) || isPinned) &&
                itemStatusState && <NewsStatusFlag state={itemStatusState} />}
              {displayAction && (
                <span>
                  {actionTextId && <FormattedMessage id={actionTextId} />}
                </span>
              )}
              <DotSeparator />
              <span>
                <FormattedTimestamp date={new Date(date)} />
              </span>
            </span>
          </section>
          {showFollowButton && (
            <FollowContainer
              pageId={pageId}
              isFollowingPage={isFollowingPage}
              className={styles.followButton}
            />
          )}
        </section>
      ) : (
        <section className={cx(styles.actor, styles.noActor)}>
          {isPinned ? (
            <NewsStatusFlag
              state="PINNED"
              className={styles.discreetStatusFlag}
            />
          ) : (
            <FormattedTimestamp date={new Date(date)} />
          )}
        </section>
      )}
    </>
  );
};

export default NewsItemActor;
